/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   3/2/22
 */

import React, {useMemo} from "react";
import {Box} from "@mui/material";
import Header from "./Header";
import Body from "./Body";
import {useSelector} from "react-redux";
import eachDay from "date-fns/eachDayOfInterval";
import {State} from "../state/store";
import { VegaEvent, Employee, GoogleEvent, Holiday, WorkdayEvent, OpsGenieEvent } from "../interfaces/Global";
import {addDays, areIntervalsOverlapping, differenceInHours} from "date-fns";
import Footer from "./Footer";

export interface MonthCalendarProps {
	employees: Employee[]
	vegaEvents: VegaEvent[]
	googleEvents: GoogleEvent[]
	workdayEvents: WorkdayEvent[]
	opsGenieEvents: OpsGenieEvent[]
	holidays: Holiday[]
}

export default function MonthCalendar(props: MonthCalendarProps) {

	const range = useSelector((s:State) => s.range);
	const days = useMemo(() => eachDay({ start: range.start, end: addDays(range.end, -1) }), [range]);
	const minDuration = useSelector((s:State) => s.settings.monthEventMinDurationHours ?? 4);
	const groupHeaderEnabled = useSelector((s:State) => s.settings.groupHeaderEnabled);
	const groupByEnabled = useSelector((s:State) => s.settings.groupBy) !== "off";
	const showBottomHeader = useSelector((s:State) => s.settings.showBottomHeader);
	const groupCapacityEnabled = useSelector((s:State) => s.settings.groupCapacityEnabled);
	const capacityEnabled = useSelector((s:State) => s.settings.capacity) !== "off";

	const vegaEvents = props.vegaEvents.filter(e => differenceInHours(e.endDatetime, e.startDatetime) >= minDuration)||[]
	const googleEvents = props.googleEvents.filter(e =>
		props.employees.some(em => em.id === e.employeeId) &&
		differenceInHours(e.endDatetime, e.startDatetime) >= minDuration &&
		areIntervalsOverlapping({ start: e.startDatetime, end: e.endDatetime }, range)
	);
	const workdayEvents = props.workdayEvents.filter(e =>
		areIntervalsOverlapping({ start: e.startDatetime, end: e.endDatetime }, range)
	);
	const opsGenieEvents = props.opsGenieEvents.filter(e =>
		differenceInHours(e.endDatetime, e.startDatetime) >= minDuration &&
		areIntervalsOverlapping({ start: e.startDatetime, end: e.endDatetime }, range)
	);
	const holidays = props.holidays.filter(h =>
		props.employees.some(em => em.countryCode === h.countryCode) &&
		areIntervalsOverlapping({ start: h.startDate, end: h.endDate }, range)
	);

	const intervalWidth = 40;
	const intervalDuration = 1440;
	const capacityCalculateResolution = 120;

	return (
		<Box m={1} sx={{ position: "relative", overflowX: "auto" }}>
			{(!groupByEnabled || !groupHeaderEnabled) &&
			<Header
				intervals={days}
				intervalWidth={intervalWidth}
				rangeResolution={40/1440}
				formats={[['MMM', 'd'], 'E']}
			/>}
			<Body
				range={range}
				intervals={days}
				intervalWidth={intervalWidth}
				intervalDuration={intervalDuration}
				rangeResolution={intervalWidth/intervalDuration}
				employees={props.employees||[]}
				vegaEvents={vegaEvents}
				googleEvents={googleEvents}
				workdayEvents={workdayEvents}
				opsGenieEvents={opsGenieEvents}
				oooEvents={{}}
				holidays={holidays}
				normalizeEventsPosition={true}
				capacityCalculateResolution={capacityCalculateResolution}
			/>
			{(!groupByEnabled || !groupHeaderEnabled) && showBottomHeader &&
				<Header
					intervals={days}
					intervalWidth={intervalWidth}
					rangeResolution={intervalWidth/intervalDuration}
					formats={[['MMM', 'd'], 'E']}
				/>}
			{(capacityEnabled && (!groupByEnabled || !groupCapacityEnabled)) &&
				<Footer
					intervals={days}
					employees={props.employees}
					vegaEvents={vegaEvents}
					googleEvents={googleEvents}
					workdayEvents={workdayEvents}
					opsGenieEvents={opsGenieEvents}
					oooEvents={{}}
					holidays={holidays}
					intervalWidth={intervalWidth}
					intervalDuration={intervalDuration}
					capacityCalculateResolution={capacityCalculateResolution}
				/>}
		</Box>
	);

}